import React, { useEffect, useContext, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";

import Button from "components/UI/Button";
import { useIntlHelper } from "hooks";
import { ReactIf } from "components/common/ReactIf";
import {
  useGetDashboardInfoMutation,
  useGetDashboardTokenMutation,
} from "services/dashboard";
import { useGetUsersListQuery } from "services/user/user";
import { useGetAdminsListQuery } from "services/admin/adminApi";
import {
  errorTrash,
  bluePencil,
  arrowBlueRight,
  close,
  arrowBlueLeft,
} from "assets/icons";
import { selectDashboardData } from "@redux/reducers/dashboardInfo";

import {
  foundationNames,
  priceOptimizationNames,
  inventoryOptimizationNames,
} from "../../../../utils/utilsDashboard";
import ClientContext from "../Context";
import Context from "../Context";
import useManageExternalLink from "../ManageExternalLink";
import useRemoveDashboard from "./useRemoveDashboard";
import useRemoveDashParentItemMenu from "./useRemoveDashParentItemMenu";
import MockedLayout from "./MockedLayout";
import MockedLayoutNew from "./MockedLayoutNew";
import MockedLayoutSubMenusNames from "./MockedLayoutSubMenusNames";
import Skeleton from "./Skeleton";
import { filterName } from "../../../../utils/utils";
import ReactGA from "react-ga4";

import "./styles.scss";

type ParentMenu = {
  id: number;
  label: string;
  new: boolean;
};

type SubItemMenu = {
  id: number;
  label: string;
  new: boolean;
};

export const DashboardContentWrapper: React.FC = () => {
  const { data: dashboardData = {} }: any = useSelector(selectDashboardData);

  const { userData, parentMenu, step, companyData, setStep } =
    useContext(ClientContext);

  const { getIntlMessage } = useIntlHelper();
  const {
    isEditMode,
    isUserDataLoaded,
    companyId,
    dashboardId,
    newParentName,
    setNewParentName,
    setNewSubItemMenuName,
    setNewIconArry,
  } = useContext(Context);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState("100%");
  const [fetchDashboardInfo]: any = useGetDashboardInfoMutation();
  const [fetchDashboardToken, { data: authTokenData }]: any =
    useGetDashboardTokenMutation();
  const manageExternalLink = useManageExternalLink();

  // State to store only emails from users list
  const [usersEmails, setUsersEmails] = useState<string[]>([]);
  // State to store only emails from admins list
  const [adminsEmails, setAdminsEmails] = useState<string[]>([]);

  // Call getUsersList API
  const { data: usersData } = useGetUsersListQuery({
    companyId: companyId,
    pageSize: 10,
    search: "",
    pageIndex: 1,
    descending: true,
    sortBy: "created_at",
    filterBy: [],
  });

  // Call getAdminsList API
  const { data: adminsData } = useGetAdminsListQuery({
    pageSize: 10,
    search: "",
    pageIndex: 1,
    descending: true,
    sortBy: "created_at",
    filterBy: [],
  });

  // Update usersEmails when usersData changes
  useEffect(() => {
    if (usersData && usersData.items) {
      const emails = usersData.items.map((item: any) => item.email);
      setUsersEmails(emails);
    }
  }, [usersData]);

  // Update adminsEmails when adminsData changes
  useEffect(() => {
    if (adminsData && adminsData.items) {
      const emails = adminsData.items.map((item: any) => item.email);
      setAdminsEmails(emails);
    }
  }, [adminsData]);

  const [parentMenuName, setParentMenuName] = useState<
    ParentMenu | string | null
  >(null);
  const [subMenuName, setSubMenuName] = useState<SubItemMenu | string | null>(
    null
  );

  const removeDashboard = useRemoveDashboard();
  const removeDashParentItemMenu = useRemoveDashParentItemMenu();

  const isIntuilizeUser = useMemo(() => {
    return (
      !!userData?.email &&
      (usersEmails.includes(userData.email) ||
        adminsEmails.includes(userData.email))
    );
  }, [userData?.email, usersEmails, adminsEmails]);

  const menuItems = parentMenu.map((menu) => ({
    id: Number(menu.id),
    label: menu.name,
    new: false,
  }));

  const showNewDashboard = () => {};

  const stepBack = () => {
    switch (step) {
      case 1:
        setParentMenuName(null);
        setStep(step - 1);
        break;
      case 2:
        setSubMenuName(null);
        setStep(step - 1);
        break;
      default:
        setParentMenuName(null);
        setSubMenuName(null);
    }
  };

  useEffect(() => {
    if (
      dashboardData &&
      dashboardData?.name &&
      companyData &&
      companyData?.name
    ) {
      const urlPattern = /\/user\/company\/(\d+)\/(\d+)/;
      const match = window.location.pathname.match(urlPattern);
      const tenantId = match ? match[1] : "";
      const dashboardId = match ? match[2] : "";

      const hostname = window.location.hostname;
      const isQAorStaging =
        hostname.includes("qa-app") || hostname.includes("stg-api");

      if (!isQAorStaging && !isIntuilizeUser) {
        ReactGA.event({
          category: companyData?.name,
          action: "Dashboard Tracking",
          label: filterName(dashboardData?.name),
          value: 1,
        });

        ReactGA.event("dashboard_interaction", {
          dashboard_name: filterName(dashboardData?.name),
          company_name: companyData?.name,
          tenant_id: tenantId,
          dashboard_id: dashboardId,
        });
      }

      if (!isQAorStaging && isIntuilizeUser) {
        ReactGA.event("intuilize_interaction", {
          dashboard_name: filterName(dashboardData?.name),
          user_name: userData?.full_name,
          user_identification: userData?.id,
          dashboard_id: dashboardId,
        });
      }
    }
  }, [dashboardData]);

  useEffect(
    () => {
      if (isUserDataLoaded && dashboardId) {
        fetchDashboardInfo({
          companyId,
          dashboardId,
        }).then(() => {
          setTimeout(() => {
            //make a request here
            fetchDashboardToken(companyId);
          }, 500);
        });
      }
    },
    // eslint-disable-next-line
    [isUserDataLoaded, dashboardId]
  );

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    try {
      const iframeDocument =
        iframe?.contentDocument || iframe?.contentWindow?.document;
      const scrollHeight = iframeDocument?.body?.scrollHeight;
      if (scrollHeight) {
        setIframeHeight(`${scrollHeight}px`);
      }
    } catch (error) {
      setIframeHeight("1200px");
    }
  };

  const renderSteps = () => {
    switch (step) {
      case 0:
        setNewIconArry("[trendingUpBlack, trendingUp]");
        return (
          <MockedLayoutNew
            menuItems={menuItems}
            setParentMenuName={setParentMenuName}
            setNewParentName={setNewParentName}
            setStep={setStep}
          />
        );
      case 1:
        return (
          <MockedLayoutSubMenusNames
            menuItems={menuItems}
            newParentName={newParentName}
            setSubMenuName={setSubMenuName}
            setNewSubItemMenuName={setNewSubItemMenuName}
            setStep={setStep}
          />
        );
      case 2:
        return <MockedLayout />;
      default:
        return (
          <div className="widget_base-info">
            <Skeleton />
          </div>
        );
    }
  };

  if (!authTokenData?.auth_token && dashboardData?.is_external_dashboard) {
    return (
      <div className="widget_base-info">
        <Skeleton />
      </div>
    );
  }

  if (authTokenData?.auth_token && isEditMode) {
    return (
      <section className="dashboard-content">
        <div className="widget_base-info">
          {step == 0 && (
            <h3 className="dashboard-title">
              {getIntlMessage("create_or_select_a_parent_menu")}
            </h3>
          )}
          {(step == 1 || step == 2) && (
            <h3 className="dashboard-title">
              {getIntlMessage("parent_menu") + ": " + parentMenuName}
            </h3>
          )}
          {step != 0 && (
            <ReactIf value={isEditMode}>
              <div className="dashboard-actions">
                <Button
                  startIcon={<img src={arrowBlueLeft} alt="back" />}
                  variant="text"
                  onClick={stepBack}
                >
                  {getIntlMessage("back")}
                </Button>
              </div>
            </ReactIf>
          )}
        </div>

        {step == 2 && (
          <div className="widget_base-info">
            <h3 className="dashboard-sub-title">
              {getIntlMessage("dashboard_name") +
                ": " +
                filterName(subMenuName)}
            </h3>
          </div>
        )}

        {renderSteps()}
      </section>
    );
  }

  //if token exist and dashboard using helico....
  if (authTokenData?.auth_token && dashboardData?.is_external_dashboard) {
    return (
      <>
        {removeDashboard.el}
        <div className="widget_base-info helico-data">
          <h3 className="dashboard-title">{filterName(dashboardData?.name)}</h3>
          <ReactIf value={isEditMode}>
            <div className="dashboard-actions">
              <Button
                startIcon={<img src={errorTrash} alt="delete dashboard" />}
                variant="text"
                color="error"
                onClick={() => removeDashboard.setOpen(true)}
              >
                {getIntlMessage("delete_dashboard")}
              </Button>

              <Button
                startIcon={<img src={bluePencil} alt="edit dashboard" />}
                variant="text"
                onClick={() => manageExternalLink.setOpen(true, true)}
              >
                {getIntlMessage("edit_dashboard")}
              </Button>
            </div>
          </ReactIf>
        </div>

        {/* eslint-disable-next-line */}
        <iframe
          id="helicoIframe"
          ref={iframeRef}
          src={`${dashboardData?.link}&mode=dashboard&authToken=${authTokenData?.auth_token}`}
          width="100%"
          height={iframeHeight}
          onLoad={handleIframeLoad}
          title="Helico"
          style={{ border: "none", paddingTop: "5px" }}
        ></iframe>
        <div>
          <div
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "9px",
              paddingTop: "5px",
              paddingBottom: "5px",
            }}
          >
            © {new Date().getFullYear()} Intuilize Inc - By using this site, you
            agree to our{" "}
            <a
              href="https://intuilize.com/terms-of-use/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#27a9e1" }}
            >
              Terms of Use & Privacy Policy
            </a>
          </div>
        </div>
        {manageExternalLink.el}
      </>
    );
  }

  return (
    <section className="dashboard-content">
      <div className="widget_base-info">
        <h3 className="dashboard-title">{filterName(dashboardData?.name)}</h3>
        <ReactIf value={isEditMode}>
          <div className="dashboard-actions">
            <Button
              startIcon={<img src={errorTrash} alt="delete dashboard" />}
              variant="text"
              color="error"
              onClick={() => removeDashboard.setOpen(true)}
            >
              {getIntlMessage("delete_dashboard")}
            </Button>
          </div>
        </ReactIf>
      </div>

      <MockedLayout />
      {removeDashboard.el}
    </section>
  );
};

export const DashboardContent = () => {
  return (
    <>
      {/* eslint-disable-next-line */}
      <iframe
        id="logout-from-helico"
        src={`${process.env.REACT_APP_THIRD_PARTY_URL}/hi-ee/j_spring_security_logout`}
        width="0px"
        height="0px"
      ></iframe>
      <DashboardContentWrapper />
    </>
  );
};
